import React from 'react';
import { Input, Form } from '@bynder/design-system';
import { Translate } from '@bynder/localization';
import FontStyleDropdown from '../../themesets/Typography/FontStyleDropdown';
import {
  useGetAvailableFontFamilies,
  useGetAvailableFontStyles,
} from '../../../queries/themesets';
import { isDevelopment } from '../../../constants/app';
import { stripQuotes } from './helpers';

export type TypographyChoice = {
  fontFamily: string;
  fontStyle: string;
};

interface Props {
  fontFamily: string;
  fontStyle: string;
  fontSize: number;
  handleFontChange: (newTypo: TypographyChoice) => void;
  handleSizeChange: (newFontSize: number) => void;
  fontSizeOrigin?: string;
  fontFamilyOrigin?: string;
  fontStyleOrigin?: string;
  children?: JSX.Element;
  canEdit: boolean;
}

const MIN_FONT_SIZE_VALUE = 1;
const MAX_FONT_SIZE_VALUE = 64;

const InspectorTypoPicker: React.FC<Props> = ({
  fontFamily,
  fontStyle,
  fontSize,
  handleFontChange,
  handleSizeChange,
  fontSizeOrigin,
  fontFamilyOrigin,
  fontStyleOrigin,
  children,
  canEdit,
}) => {
  const [selectedFontFamily, setSelectedFontFamily] = React.useState(
    stripQuotes(fontFamily),
  );
  const [selectedFontStyle, setSelectedFontStyle] = React.useState(
    stripQuotes(fontStyle),
  );
  const [selectedFontSize, setSelectedFontSize] = React.useState(fontSize);

  const handleFontSizeChange = (newFontSize: number) => {
    setSelectedFontSize(newFontSize);

    if (
      newFontSize < MIN_FONT_SIZE_VALUE ||
      newFontSize > MAX_FONT_SIZE_VALUE
    ) {
      return;
    }
    handleSizeChange(newFontSize);
  };

  const handleFontFamilyChange = (newValue: string) => {
    setSelectedFontFamily(newValue);
  };

  const handleFontStyleChange = (newFontStyle: string) => {
    setSelectedFontStyle(newFontStyle);
    handleFontChange({
      fontFamily: selectedFontFamily,
      fontStyle: newFontStyle,
    });
  };

  React.useEffect(() => {
    setSelectedFontFamily(stripQuotes(fontFamily));
    setSelectedFontStyle(stripQuotes(fontStyle));
    setSelectedFontSize(fontSize);
  }, [fontStyle, fontFamily, fontSize]);

  return (
    <>
      <FontFamilyPicker
        canEdit={canEdit}
        selectedFontFamily={selectedFontFamily}
        setSelectedFontFamily={handleFontFamilyChange}
      >
        <PropertyOrigin origin={fontFamilyOrigin} />
      </FontFamilyPicker>
      <Form.Row>
        <FontStylePicker
          canEdit={canEdit}
          selectedFontFamily={selectedFontFamily}
          selectedFontStyle={selectedFontStyle}
          setSelectedFontStyle={handleFontStyleChange}
        >
          <PropertyOrigin origin={fontStyleOrigin} />
        </FontStylePicker>
        <FontSizeInput
          canEdit={canEdit}
          fontSize={selectedFontSize}
          setFontSize={handleFontSizeChange}
        >
          <PropertyOrigin origin={fontSizeOrigin} />
        </FontSizeInput>
      </Form.Row>
      {children}
    </>
  );
};

export default InspectorTypoPicker;

export const FontFamilyPicker: React.FC<{
  canEdit: boolean;
  children?: React.ReactNode;
  selectedFontFamily?: string;
  setSelectedFontFamily: (fontFamily: string) => void;
}> = ({ canEdit, children, selectedFontFamily, setSelectedFontFamily }) => {
  const fontFamilyOptions = useGetAvailableFontFamilies();
  return (
    <Form.Group>
      <Form.Label id="label-family">
        <Translate id="THEMESET_TYPO_PICKER.FONT_FAMILY" />
      </Form.Label>
      {canEdit ? (
        <FontStyleDropdown
          options={fontFamilyOptions}
          selectedValue={selectedFontFamily}
          newValueSelectedFn={setSelectedFontFamily}
        />
      ) : (
        <div>{selectedFontFamily}</div>
      )}
      {children}
    </Form.Group>
  );
};

export const FontStylePicker: React.FC<{
  canEdit: boolean;
  selectedFontFamily?: string;
  selectedFontStyle?: string;
  setSelectedFontStyle: (fontStyle: string) => void;
  children?: React.ReactNode;
}> = ({
  canEdit,
  selectedFontFamily,
  setSelectedFontStyle,
  selectedFontStyle,
  children,
}) => {
  const fontStyleOptions = useGetAvailableFontStyles(selectedFontFamily);
  return (
    <Form.Group>
      <Form.Label id="label-font-style" data-testid="font-style-picker">
        <Translate id="THEMESET_TYPO_PICKER.FONT_STYLE" />
      </Form.Label>
      {canEdit ? (
        <FontStyleDropdown
          options={fontStyleOptions}
          selectedValue={selectedFontStyle}
          newValueSelectedFn={setSelectedFontStyle}
        />
      ) : (
        <div>{selectedFontStyle}</div>
      )}
      {children}
      {!fontStyleOptions.includes(selectedFontStyle) && (
        <Form.HelperText
          id={'label-font-style'}
          icon={<Form.IconError />}
          isInvalid
        >
          Choose one
        </Form.HelperText>
      )}
    </Form.Group>
  );
};

export const FontSizeInput: React.FC<{
  canEdit: boolean;
  fontSize: number;
  setFontSize: (size: number) => void;
  children?: React.ReactNode;
}> = ({ canEdit, fontSize, setFontSize, children }) => {
  const handleFontSizeChange = (value: string) => {
    if (value === '') {
      setFontSize(0);
      return;
    }
    const newSize = parseInt(value, 10);
    if (!isNaN(newSize)) {
      setFontSize(newSize);
    }
  };
  return (
    <Form.Group>
      <Form.Label id="label-weight">
        <Translate id="TYPOGRAPHY_EXPANDED.FONT_SIZE" />
      </Form.Label>
      {canEdit ? (
        <>
          <Input
            suffix="px"
            type="number"
            value={fontSize.toString()}
            onChange={handleFontSizeChange}
            aria-labelledby="label-weight"
          />
        </>
      ) : (
        <div>{fontSize}px</div>
      )}
      <>
        {fontSize < MIN_FONT_SIZE_VALUE && (
          <Form.HelperText>Min size {MIN_FONT_SIZE_VALUE}px</Form.HelperText>
        )}
        {fontSize > MAX_FONT_SIZE_VALUE && (
          <Form.HelperText>Max size {MAX_FONT_SIZE_VALUE}px</Form.HelperText>
        )}
        {children}
      </>
    </Form.Group>
  );
};

export const PropertyOrigin: React.FC<{ origin: string }> = ({ origin }) => {
  return (
    isDevelopment &&
    origin && <Form.HelperText>{`origin: ${origin}`}</Form.HelperText>
  );
};
